import moment from 'moment';
import { get } from 'lodash/fp';

import 'moment-timezone';
import {
  CrmType,
  FNIType,
  STATES,
  Language,
  DateFormat,
  STATES_CA,
  CurrencyType,
  FuelEfficiencyUnit,
  HOUR_FORMATS,
  COUNTRIES,
  SpecialFees,
  FixedValueFee,
  InsuranceProvider,
  FeeCategory,
  InsuranceRequestType,
  ExtendedFinanceType,
  LendingFlowType,
} from '@upstart/harmony/lib/constants';
import {
  CreditRatingMax,
  CreditRatingMin,
  DealerType,
} from '@upstart/harmony/lib/legos/constants';
import {
  UNIT_LABELS,
  SUPPORTED_LANGUAGE_LABELS,
  COUNTRIES_LABELS,
  HOUR_FORMAT_LABELS,
} from '../DealerLocalisationConstants';
import {
  SPECIAL_FEES_NAMES,
  OnlineInsurancePages,
  InstoreInsurancePages,
} from '../../../util/constants';

export * from './schemaStep4';
export * from './schemaStep8';

const reviewLogo = {
  'Cars.com': 'https://prodigy-cdn.upstart.com/dealers/images/carsdotcom.png',
  'Auto.com': 'https://prodigy-cdn.upstart.com/dealers/images/autologo.png',
  CarGurus: 'https://prodigy-cdn.upstart.com/dealers/images/cargurus.png',
  Carfax: 'https://prodigy-cdn.upstart.com/dealers/images/carfax.jpeg',
  Facebook: 'https://prodigy-cdn.upstart.com/dealers/images/facebook.png',
  Google: 'https://prodigy-cdn.upstart.com/dealers/images/google.png',
  KBB: 'https://prodigy-cdn.upstart.com/dealers/images/kbb.png',
  'Dealer Rater':
    'https://prodigy-cdn.upstart.com/dealers/images/dealerrater.png',
  Yelp: 'https://prodigy-cdn.upstart.com/dealers/images/yelp.png',
};

export const getDealerType = {
  title: 'Build and Price',
  type: 'string',
  enum: Object.values(DealerType),
  enumNames: [
    'Build and Price Disabled',
    'Build and Price Enabled',
    'Build and Price Only',
  ],
};

export const newDealerSchema = {
  title: 'Dealer Specifications',
  type: 'object',
  required: ['name'],
  properties: {
    name: { type: 'string', title: 'Name' },
    isIndependent: { type: 'boolean', title: 'Independent Dealer' },
    brand: { type: 'array', title: 'Brand' },
    dealerType: getDealerType,
  },
};

// Dealer level dependency objects stored here for readability (otherwise the scehma would be extremly nested)
export const getStep1Schema = (groups, dealer) => ({
  title: 'Dealer Specifications',
  type: 'object',
  required: ['name', 'countryToggle', 'country'],
  properties: {
    name: {
      type: 'string',
      title: 'Name',
    },
    dealergroup_id: {
      type: 'string',
      title: 'Group',
      enum: groups.map(({ id }) => id),
      enumNames: groups.map(({ id, name }) => name || id),
    },
    logoFile: {
      type: 'string',
      title: 'Logo Upload',
      format: 'data-url',
    },
    stickerLogoFile: {
      type: 'string',
      title: 'Sticker Logo',
      format: 'data-url',
    },
    isIndependent: {
      type: 'boolean',
      title: 'Independent Dealer',
    },
    brand: {
      type: 'array',
      title: 'Brand',
    },
    dealerType: getDealerType,
    signedOn: {
      type: 'string',
      title: 'Signed On',
      format: 'alt-date',
    },
    liveAt: {
      type: 'string',
      title: 'Live At',
      format: 'alt-date',
    },
    cancelledAt: {
      type: 'string',
      title: 'Cancelled At',
      format: 'alt-date',
    },
    dsm: {
      type: 'string',
      title: 'DSM',
    },
    contactEmail: {
      type: 'string',
      title: 'Main Point of Contact Email',
    },
    financeEmail: {
      type: 'string',
      title: 'Finance Email',
    },
    // countryToggle is the visible option to switch between countries
    countryToggle: {
      type: 'string',
      title: 'Country',
      enum: Object.values(COUNTRIES),
      enumNames: Object.values(COUNTRIES).map(
        country => COUNTRIES_LABELS[country],
      ),
    },
    // country is the actual value to be passed to backend
    country: {
      type: 'string',
      title: 'Country',
      enum: Object.values(COUNTRIES),
      enumNames: Object.values(COUNTRIES).map(key => COUNTRIES[key]),
      hidden: true,
    },
    privacyPolicyUrl: {
      type: 'string',
      title: 'Privacy Policy URL',
    },
  },
  dependencies: {
    country: {
      oneOf: [
        {
          properties: {
            country: {
              enum: [COUNTRIES.US],
            },
            address: {
              type: 'string',
              title: 'Address',
            },
            city: {
              type: 'string',
              title: 'City',
            },
            state: {
              type: 'string',
              title: 'State',
              enum: Object.keys(STATES),
              enumNames: Object.keys(STATES).map(key => STATES[key]),
            },
            county: {
              type: 'string',
              title: 'County',
            },
            zip: {
              type: 'string',
              title: 'Zip',
            },
            phone: {
              type: 'string',
              title: 'Phone',
            },
          },
          required:
            dealer?.dealerType === DealerType.BYO
              ? ['address', 'city', 'state', 'county', 'zip', 'phone']
              : ['address', 'city', 'state', 'county', 'zip'],
        },
        {
          properties: {
            country: {
              enum: [COUNTRIES.CA],
            },
            address: {
              type: 'string',
              title: 'Address',
            },
            city: {
              type: 'string',
              title: 'City',
            },
            state: {
              type: 'string',
              title: 'Province',
              enum: Object.keys(STATES_CA),
              enumNames: Object.keys(STATES_CA).map(key => STATES_CA[key]),
            },
            zip: {
              type: 'string',
              title: 'Postal Code',
            },
            phone: {
              type: 'string',
              title: 'Phone',
            },
          },
          required: ['address', 'city', 'state', 'zip'],
        },
      ],
    },
  },
});

export const step2Schema = {
  title: 'Dealer Value Guarantee',
  type: 'object',
  properties: {
    valueGuarantee: {
      type: 'object',
      title: '',
      default: {},
      properties: {
        reviews: {
          type: 'array',
          title: 'Reviews',
          minItems: 0,
          maxItems: 3,
          items: {
            type: 'object',
            required: ['logo', 'rating', 'reviews'],
            properties: {
              logo: {
                type: 'string',
                title: 'Logo',
                enum: Object.values(reviewLogo),
                enumNames: Object.keys(reviewLogo),
              },
              rating: {
                type: 'number',
                title: 'Rating',
                minimum: 1,
                maximum: 5,
              },
              reviews: {
                type: 'number',
                title: 'Number of Reviews',
                minimum: 0,
                maximum: 100000,
              },
            },
          },
        },
        description: {
          type: 'array',
          title: 'Description',
          minItems: 0,
          maxItems: 8,
          items: {
            type: 'string',
          },
        },
      },
    },
  },
};

export const getStep3Schema = (feeds, feedviews) => ({
  title: 'Inventory',
  type: 'object',
  properties: {
    inStoreFeedview_id: {
      type: 'string',
      title: 'Instore Feedview',
      enum: feedviews.map(({ id }) => id),
      enumNames: feedviews.map(({ id, name }) => name || id),
    },
    onlineFeedview_id: {
      type: 'string',
      title: 'Default Online Feedview',
      enum: feedviews.map(({ id }) => id),
      enumNames: feedviews.map(({ id, name }) => name || id),
    },
    feeds: {
      type: 'array',
      title: 'Inventory Feeds',
      items: {
        type: 'string',
        enum: feeds.map(f => f.id),
        enumNames: feeds.map(f => `${f.name} [${f.config?.fileName || ''}]`),
      },
      uniqueItems: true,
    },
    canImportInventory: {
      type: 'boolean',
      default: true,
      title: 'Can Import Inventory?',
    },
  },
});

export const step5Schema = {
  title: 'Notifications',
  type: 'object',
  properties: {
    managers: {
      type: 'array',
      title: 'Managers',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          email: { type: 'string', title: 'Email' },
          phone: { type: 'string', title: 'Phone' },
          shouldSendTurnover: { type: 'boolean', title: 'Send TO' },
          shouldSendLead: { type: 'boolean', title: 'Send Lead Emails' },
          shouldSendRemoteTestDrive: {
            type: 'boolean',
            title: 'Send remote test drive',
          },
          shouldSendPickup: {
            type: 'boolean',
            title: 'Send Pickup',
          },
          shouldSendDelivery: {
            type: 'boolean',
            title: 'Send Delivery',
          },
          shouldSendTestDrive: {
            type: 'boolean',
            title: 'Send Test Drive',
          },
          shouldSendCreditApplication: {
            type: 'boolean',
            title: 'Send Credit App',
          },
          shouldReceiveSignatures: {
            type: 'boolean',
            title: "Should be CC'd on E-contracting",
          },
          isPrimaryNewSigner: {
            type: 'boolean',
            title:
              'Primary New Vehicle Signer for E-contracting (Limit 1 per dealer)',
          },
          isPrimaryUsedSigner: {
            type: 'boolean',
            title:
              'Primary Used Vehicle Signer for E-contracting (Limit 1 per dealer)',
          },
        },
      },
    },
    disabledNotifications: {
      type: 'object',
      title: 'Enabled notifications',
      // UI schema uses reverse toggle button which is active when the value is false.
      properties: {
        emails: {
          type: 'object',
          title: 'Emails',
          properties: {
            testDrive: {
              type: 'boolean',
              default: false,
              title: 'Remote Test Drive',
            },
            testDriveIsWaiting: {
              type: 'boolean',
              default: false,
              title: 'Test Drive Is waiting',
            },
            newCarIsWaiting: {
              type: 'boolean',
              default: false,
              title: 'Pickup/Delivery',
            },
          },
        },
      },
    },
  },
};

export const getStep6Schema = dealer => {
  const newCarFeeProperties = {
    cash: {
      type: 'object',
      title: '',
      properties: {
        [FeeCategory.TAXABLE]: {
          title: 'New Cars - Cash - Taxable Fees',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.NON_TAXABLE]: {
          title: 'New Cars - Cash - Non Taxable Fees',
          $ref: '#/definitions/typedFee',
        },
      },
    },
    purchase: {
      type: 'object',
      title: '',
      properties: {
        [FeeCategory.TAXABLE]: {
          title: 'New Cars - Purchase - Taxable Fees',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.NON_TAXABLE]: {
          title: 'New Cars - Purchase - Non Taxable Fees',
          $ref: '#/definitions/typedFee',
        },
      },
    },
    lease: {
      type: 'object',
      title: '',
      properties: {
        [FeeCategory.TAXABLE]: {
          title: 'New Cars - Lease - Taxable Fees',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.NON_TAXABLE]: {
          title: 'New Cars - Lease - Non Taxable Fees',
          // incremental fees are supported for lease option in oregon
          $ref: '#/definitions/typedFee',
        },
        [FeeCategory.CAPITALIZED_COST]: {
          title: 'New Cars - Lease - In Capitalized Cost',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.CAPITALIZED_COST_TAXABLE]: {
          title: 'New Cars - Lease - Taxable In Capitalized Cost',
          $ref: '#/definitions/valueFee',
        },
      },
    },
  };
  if (
    dealer?.desking?.enableOnePayLease &&
    dealer?.desking?.differentOnePayFeesFromLease
  ) {
    newCarFeeProperties[ExtendedFinanceType.ONE_PAY_LEASE] = {
      type: 'object',
      title: '',
      properties: {
        [FeeCategory.TAXABLE]: {
          title: 'New Cars - One Pay Lease - Taxable Fees',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.NON_TAXABLE]: {
          title: 'New Cars - One Pay Lease - Non Taxable Fees',
          // incremental fees and not currently supported for lease
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.CAPITALIZED_COST]: {
          title: 'New Cars - One Pay Lease - In Capitalized Cost',
          $ref: '#/definitions/valueFee',
        },
        [FeeCategory.CAPITALIZED_COST_TAXABLE]: {
          title: 'New Cars - One Pay Lease - Taxable In Capitalized Cost',
          $ref: '#/definitions/valueFee',
        },
      },
    };
  }
  // Only add CRM Field for VINSolutions dealers
  // https://getprodigy.atlassian.net/browse/REL-3444
  const valueFeeProperties = {
    name: { type: 'string', title: 'Name' },
    value: { type: 'number', title: 'Value' },
    includeInFeeStack: {
      type: 'boolean',
      title: 'Include in "Fees" stack',
      enum: [true, false],
      default: true,
    },
  };

  const typedFeeProperties = {
    type: {
      type: 'string',
      enum: [FixedValueFee, ...Object.values(SpecialFees)],
      enumNames: [
        'Fixed Amount',
        ...Object.values(SpecialFees).map(
          feeType => SPECIAL_FEES_NAMES[feeType],
        ),
      ],
      title: 'Type',
      default: FixedValueFee,
    },
    name: { type: 'string', title: 'Name' },
    apr: {},
    value: {},
    includeInFeeStack: {
      default: true,
    },
  };

  if (get('crmType')(dealer) === CrmType.VINSOLUTIONS) {
    const crmField = {
      type: 'string',
      title: 'CRM Field (VINSolutions Only)',
    };

    typedFeeProperties.crmField = crmField;
    valueFeeProperties.crmField = crmField;
  }

  return {
    definitions: {
      valueFee: {
        type: 'array',
        items: {
          type: 'object',
          required: ['name'],
          properties: valueFeeProperties,
        },
      },
      typedFee: {
        type: 'array',
        items: {
          type: 'object',
          required: ['name'],
          properties: typedFeeProperties,
          dependencies: {
            type: {
              oneOf: [
                {
                  properties: {
                    type: {
                      enum: [FixedValueFee],
                    },
                    value: { type: 'number', title: 'Value' },
                    includeInFeeStack: {
                      type: 'boolean',
                      title: 'Include in "Fees" stack',
                      enum: [true, false],
                    },
                  },
                },
                {
                  properties: {
                    type: {
                      enum: Object.values(SpecialFees),
                    },
                    apr: { type: 'number', title: 'Rate' },
                    includeInFeeStack: {
                      type: 'boolean',
                      title: 'Include in "Fees" stack',
                      enum: [true, false],
                    },
                  },
                },
              ],
            },
          },
        },
      },
    },
    title: 'Fees',
    type: 'object',
    required: [],
    properties: {
      translationsToBeSaved: {
        type: 'object',
        title: '',
        properties: {
          dealerFeesEnglish: {
            type: 'string',
            title: 'Default Name for Dealer Fees',
            default: 'Fees',
            maxLength: 25,
          },
          dealerFeesSpanish: {
            type: 'string',
            title: 'Default Name for Dealer Fees (Spanish)',
            default: 'Cargos del Distribuidor',
            maxLength: 25,
          },
        },
      },
      fees: {
        type: 'object',
        title: '',
        properties: {
          New: {
            type: 'object',
            title: '',
            properties: newCarFeeProperties,
          },
          Used: {
            type: 'object',
            title: '',
            properties: {
              cash: {
                type: 'object',
                title: '',
                properties: {
                  [FeeCategory.TAXABLE]: {
                    title: 'Used Cars - Cash - Taxable Fees',
                    $ref: '#/definitions/valueFee',
                  },
                  [FeeCategory.NON_TAXABLE]: {
                    title: 'Used Cars - Cash - Non Taxable Fees',
                    $ref: '#/definitions/typedFee',
                  },
                },
              },
              purchase: {
                type: 'object',
                title: '',
                properties: {
                  [FeeCategory.TAXABLE]: {
                    title: 'Used Cars - Purchase - Taxable Fees',
                    $ref: '#/definitions/valueFee',
                  },
                  [FeeCategory.NON_TAXABLE]: {
                    title: 'Used Cars - Purchase - Non Taxable Fees',
                    $ref: '#/definitions/typedFee',
                  },
                },
              },
            },
          },
          ...(dealer?.featureConfig?.useCPO?.enabled && {
            CPO: {
              type: 'object',
              title: '',
              properties: {
                cash: {
                  type: 'object',
                  title: '',
                  properties: {
                    [FeeCategory.TAXABLE]: {
                      title: 'CPO Cars - Cash - Taxable Fees',
                      $ref: '#/definitions/valueFee',
                    },
                    [FeeCategory.NON_TAXABLE]: {
                      title: 'CPO Cars - Cash - Non Taxable Fees',
                      $ref: '#/definitions/typedFee',
                    },
                  },
                },
                purchase: {
                  type: 'object',
                  title: '',
                  properties: {
                    [FeeCategory.TAXABLE]: {
                      title: 'CPO Cars - Purchase - Taxable Fees',
                      $ref: '#/definitions/valueFee',
                    },
                    [FeeCategory.NON_TAXABLE]: {
                      title: 'CPO Cars - Purchase - Non Taxable Fees',
                      $ref: '#/definitions/typedFee',
                    },
                  },
                },
              },
            },
          }),
        },
      },
    },
  };
};

export const step7Schema = {
  definitions: {
    content: {
      title: 'Content',
      type: 'array',
      items: {
        type: 'object',
        required: ['headline', 'term', 'cost'],
        properties: {
          headline: { type: 'string', title: 'Subtitle', maxLength: 100 },
          term: { type: 'string', title: 'Term', maxLength: 150 },
          deductible: { type: 'number', title: 'Deductible' },
          cost: { type: 'number', title: 'Cost' },
        },
      },
    },
    body: {
      type: 'array',
      items: {
        type: 'object',
        required: ['title', 'content'],
        properties: {
          title: { type: 'string', title: 'Title', maxLength: 100 },
          content: {
            $ref: '#/definitions/content',
          },
        },
      },
    },
  },
};

export const getStep9Schema = () => ({
  title: 'Localisation',
  type: 'object',
  required: ['timezone'],
  properties: {
    localizationConfig: {
      type: 'object',
      title: '',
      default: {},
      properties: {
        supportedLanguages: {
          type: 'array',
          title: 'Supported Languages',
          items: {
            enum: Object.values(Language),
            enumNames: Object.values(Language).map(
              language => SUPPORTED_LANGUAGE_LABELS[language],
            ),
          },
          uniqueItems: true,
        },
        defaultLanguage: {
          type: 'string',
          title: 'Default Language',
          enum: Object.values(Language),
          enumNames: Object.values(Language).map(
            language => SUPPORTED_LANGUAGE_LABELS[language],
          ),
          uniqueItems: true,
        },
        dateFormat: {
          type: 'string',
          title: 'Date Format',
          enum: Object.values(DateFormat),
          enumNames: Object.values(DateFormat),
          uniqueItems: true,
        },
        hourFormat: {
          type: 'string',
          title: 'Hour Format',
          enum: Object.values(HOUR_FORMATS),
          enumNames: Object.values(HOUR_FORMATS).map(
            format => HOUR_FORMAT_LABELS[format],
          ),
          uniqueItems: true,
          hidden: true, // temporarily hiding this feature until we want to fully support 24 hour format
        },
        fuelEfficiencyUnit: {
          type: 'string',
          title: 'Units',
          enum: Object.values(FuelEfficiencyUnit),
          enumNames: Object.values(FuelEfficiencyUnit).map(
            unit => UNIT_LABELS[unit],
          ),
          uniqueItems: true,
        },
        currency: {
          type: 'string',
          title: 'Currency',
          enum: Object.values(CurrencyType),
          enumNames: Object.keys(CurrencyType),
          uniqueItems: true,
        },
      },
      required: [
        'supportedLanguages',
        'defaultLanguage',
        'dateFormat',
        // 'hourFormat', // temporarily hiding this feature until we want to fully support 24 hour format
        'fuelEfficiencyUnit',
        'currency',
      ],
    },
    timezone: {
      type: 'string',
      title: 'Time Zone',
      enum: moment.tz.names(),
    },
  },
});

const getInsuranceConfForProvider = (
  insuranceRequestTypes = Object.values(InsuranceRequestType),
) => ({
  online: {
    title: '',
    type: 'object',
    properties: {
      enabled: {
        title: 'Online',
        type: 'boolean',
        enum: [true, false],
        default: false,
      },
    },
    dependencies: {
      enabled: {
        oneOf: [
          {
            properties: { enabled: { enum: [false] } },
          },
          {
            properties: {
              enabled: { enum: [true] },
              ...OnlineInsurancePages.reduce((acc, step) => {
                acc[step.id] = {
                  title: '',
                  type: 'object',
                  properties: {
                    enabled: {
                      title: step.name,
                      type: 'boolean',
                      enum: [true, false],
                      default: true,
                    },
                  },
                };
                return acc;
              }, {}),
            },
          },
        ],
      },
    },
  },
  instore: {
    title: '',
    type: 'object',
    properties: {
      enabled: {
        title: 'In-Store',
        type: 'boolean',
        enum: [true, false],
        default: false,
      },
    },
    dependencies: {
      enabled: {
        oneOf: [
          {
            properties: { enabled: { enum: [false] } },
          },
          {
            properties: {
              enabled: { enum: [true] },
              requestType: {
                title: '',
                type: 'string',
                enum: insuranceRequestTypes,
                default: 'text',
              },
              ...insuranceRequestTypes.reduce(
                (allTypes, type) => ({
                  ...allTypes,
                  [type]: {
                    title: '',
                    type: 'object',
                    properties: {
                      ...InstoreInsurancePages.reduce((acc, step) => {
                        acc[step.id] = {
                          title: '',
                          type: 'object',
                          properties: {
                            enabled: {
                              title: step.name,
                              type: 'boolean',
                              enum: [true, false],
                              default: true,
                            },
                          },
                        };
                        return acc;
                      }, {}),
                    },
                  },
                }),
                {},
              ),
            },
          },
        ],
      },
    },
  },
});

export const step10Schema = {
  title: 'Insurance',
  type: 'object',
  properties: {
    insurance: {
      type: 'object',
      title: '',
      default: {},
      required: ['provider'],
      dependencies: {
        provider: {
          oneOf: [
            {
              properties: {
                provider: { enum: [InsuranceProvider.COVER] },
                ...getInsuranceConfForProvider(),
              },
            },
            {
              properties: {
                provider: { enum: [InsuranceProvider.JERRY] },
                ...getInsuranceConfForProvider([InsuranceRequestType.TEXT]),
              },
            },
            {
              required: ['insuranceEmail'],
              properties: {
                provider: { enum: [InsuranceProvider.ROOT] },
                insuranceEmail: {
                  type: 'string',
                  title: 'Insurance Email',
                },
                ...getInsuranceConfForProvider(),
              },
            },
          ],
        },
      },
      properties: {
        provider: {
          type: 'string',
          title: 'Insurance Provider',
          enum: Object.values(InsuranceProvider),
          enumNames: Object.values(InsuranceProvider).map(provider => {
            switch (provider) {
              case InsuranceProvider.COVER:
                return 'Cover';
              case InsuranceProvider.ROOT:
                return 'Root';
              case InsuranceProvider.JERRY:
                return 'Jerry';
              default:
                return provider;
            }
          }),
        },
      },
    },
  },
};

export const getStep11Schema = formData => {
  const enabled =
    formData?.lending?.online?.enabled ||
    formData?.lending?.instore?.enabled ||
    formData?.lending?.digitalFinance?.enabled;
  const onlyOnlineOrInstoreEnabled =
    formData?.lending?.online?.enabled || formData?.lending?.instore?.enabled;

  return {
    title: 'Lending',
    type: 'object',
    properties: {
      lending: {
        type: 'object',
        title: '',
        default: {},
        properties: {
          online: {
            title: '',
            type: 'object',
            properties: {
              enabled: {
                title: 'Online',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: { enum: [false] },
                    },
                  },
                  {
                    properties: {
                      enabled: { enum: [true] },
                      aprMarkup: {
                        title: 'Set Markup %',
                        type: 'number',
                        minimum: 0,
                        maximum: 100,
                      },
                    },
                  },
                ],
              },
            },
          },
          instore: {
            title: '',
            type: 'object',
            properties: {
              enabled: {
                title: 'In-Store',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: { enum: [false] },
                    },
                  },
                  {
                    required: ['flowType'],
                    properties: {
                      enabled: { enum: [true] },
                      flowType: {
                        title: 'Flow',
                        type: 'string',
                        default: LendingFlowType.AUTOMATIC,
                        anyOf: [
                          {
                            type: 'string',
                            title: 'Automated decisioning',
                            enum: [LendingFlowType.AUTOMATIC],
                          },
                          {
                            type: 'string',
                            title: "Manager's approval",
                            enum: [LendingFlowType.MANAGER],
                          },
                        ],
                      },
                      enableAcceptOfferScreen: {
                        title:
                          'Would you like to enable accept offer screen as a  part of the lending flow?',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                      ...(formData.fniType === FNIType.DEALERTRACK ||
                      formData.fniType === FNIType.ROUTEONE
                        ? {
                            manualFISubmission: {
                              title: 'Manual F&I Submission - In-Store',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                          }
                        : {}),
                    },
                    dependencies: {
                      flowType: {
                        oneOf: [
                          {
                            properties: {
                              flowType: { enum: [LendingFlowType.AUTOMATIC] },
                              aprMarkup: {
                                title: 'Set Markup %',
                                type: 'number',
                                minimum: 0,
                                maximum: 100,
                              },
                            },
                          },
                          {
                            properties: {
                              flowType: { enum: [LendingFlowType.MANAGER] },
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              },
              manualFISubmission: {
                oneOf: [
                  {
                    properties: {
                      manualFISubmission: { enum: [false] },
                    },
                  },
                  {
                    properties: {
                      manualFISubmission: { enum: [true] },
                      submitToSelectedLenders: {
                        title: 'Submit to Selected Lenders',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                  },
                ],
              },
            },
          },
          digitalFinance: {
            title: '',
            type: 'object',
            properties: {
              enabled: {
                title: 'Digital Finance',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            ...(formData.fniType === FNIType.DEALERTRACK ||
            formData.fniType === FNIType.ROUTEONE
              ? {
                  dependencies: {
                    enabled: {
                      oneOf: [
                        {
                          properties: {
                            enabled: { enum: [false] },
                          },
                        },
                        {
                          properties: {
                            enabled: { enum: [true] },
                            manualFISubmission: {
                              title: 'Manual F&I Submission - Digital Finance',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                          },
                        },
                      ],
                    },
                    ...(formData.fniType === FNIType.ROUTEONE
                      ? {
                          manualFISubmission: {
                            oneOf: [
                              {
                                properties: {
                                  manualFISubmission: { enum: [false] },
                                },
                              },
                              {
                                properties: {
                                  manualFISubmission: { enum: [true] },
                                  submitToSelectedLenders: {
                                    title: 'Submit to Selected Lenders',
                                    type: 'boolean',
                                    enum: [true, false],
                                    default: false,
                                  },
                                },
                              },
                            ],
                          },
                        }
                      : {}),
                  },
                }
              : {}),
          },
          ...(enabled && onlyOnlineOrInstoreEnabled
            ? {
                westlake: {
                  title: 'West Lake',
                  type: 'object',
                  properties: {
                    enabled: {
                      title: 'Enabled',
                      type: 'boolean',
                      enum: [true, false],
                      default: false,
                    },
                  },
                  dependencies: {
                    enabled: {
                      oneOf: [
                        {
                          properties: {
                            enabled: {
                              enum: [false],
                            },
                          },
                        },
                        {
                          required: ['dealerId'],
                          properties: {
                            enabled: {
                              enum: [true],
                            },
                            dealerId: {
                              title: 'Dealer ID',
                              type: 'string',
                            },
                            creditTiers: {
                              title: 'Credit Tiers',
                              type: 'object',
                              properties: {
                                low: {
                                  title: 'Low Range',
                                  type: 'number',
                                  default: 500,
                                  minimum: CreditRatingMin,
                                  maximum: CreditRatingMax,
                                  multipleOf: 1,
                                },
                                high: {
                                  title: 'High Range',
                                  type: 'number',
                                  default: 850,
                                  minimum: CreditRatingMin,
                                  maximum: CreditRatingMax,
                                  multipleOf: 1,
                                },
                              },
                            },
                          },
                        },
                      ],
                    },
                  },
                },
              }
            : {}),
          ...(enabled && onlyOnlineOrInstoreEnabled
            ? {
                upstart: {
                  title: 'Upstart',
                  type: 'object',
                  properties: {
                    enabled: {
                      title: 'Enabled',
                      type: 'boolean',
                      enum: [true, false],
                      default: false,
                    },
                  },
                  dependencies: {
                    enabled: {
                      oneOf: [
                        {
                          properties: {
                            enabled: {
                              enum: [false],
                            },
                          },
                        },
                        {
                          properties: {
                            enabled: {
                              enum: [true],
                            },
                            creditTiers: {
                              title: 'Credit Tiers',
                              type: 'object',
                              properties: {
                                low: {
                                  title: 'Low Range',
                                  type: 'number',
                                  minimum: CreditRatingMin,
                                  maximum: CreditRatingMax,
                                  multipleOf: 1,
                                  default: 500,
                                },
                                high: {
                                  title: 'High Range',
                                  type: 'number',
                                  minimum: CreditRatingMin,
                                  maximum: CreditRatingMax,
                                  multipleOf: 1,
                                  default: 850,
                                },
                              },
                            },
                            upstartCreditFirst: {
                              title: 'Upstart offers through credit app.',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                            standaloneLending: {
                              title: 'Upstart Standalone Lending',
                              type: 'object',
                              properties: {
                                enabled: {
                                  title: 'Enabled',
                                  type: 'boolean',
                                  enum: [true, false],
                                  default: false,
                                },
                              },
                            },
                          },
                        },
                      ],
                    },
                  },
                },
              }
            : {}),
          // Optional Invoice/Book value configuration is only available to dealers using RouteOne
          ...(enabled && formData.fniType === FNIType.ROUTEONE
            ? {
                allowOptionalInvoiceOrBookValue: {
                  title: 'Allow optional Invoice and Book Value',
                  type: 'boolean',
                  enum: [true, false],
                  default: false,
                },
              }
            : {}),
          ...(enabled && onlyOnlineOrInstoreEnabled
            ? {
                creditWidget: {
                  title: 'Credit Widget',
                  type: 'object',
                  required: [],
                  properties: {
                    online: {
                      title: 'Online',
                      type: 'object',
                      properties: {
                        enabled: {
                          title: 'Enabled',
                          type: 'boolean',
                          enum: [true, false],
                          default: false,
                        },
                      },
                    },
                    instore: {
                      title: 'In-Store',
                      type: 'object',
                      properties: {
                        enabled: {
                          title: 'Enabled',
                          type: 'boolean',
                          enum: [true, false],
                          default: false,
                        },
                      },
                    },
                  },
                },
              }
            : {}),
        },
      },
    },
  };
};

export const ownershipOptionsSchema = {
  title: 'Ownership Options Rules',
};

export const getStep12Schema = translationRules => ({
  type: 'object',
  title: 'Translation Rules',
  properties: {
    translationRules: {
      type: 'object',
      title: '',
      properties: {
        instore: {
          type: 'object',
          title: 'Instore',
          default: { enabled: false },
          properties: {
            enabled: {
              title: 'Use Custom Translation Override Rules',
              type: 'boolean',
              enum: [true, false],
              default: false,
            },
          },
          dependencies: {
            enabled: {
              oneOf: [
                {
                  properties: { enabled: { enum: [false] } },
                },
                {
                  properties: {
                    enabled: { enum: [true] },
                    rulesId: {
                      title: 'Translation Rules',
                      type: 'string',
                      default: '',
                      enum: ['', ...translationRules.map(({ id }) => id)],
                      enumNames: [
                        'None',
                        ...translationRules.map(
                          ({ id, name }) => `${name} (${id})`,
                        ),
                      ],
                    },
                  },
                },
              ],
            },
          },
        },
      },
    },
  },
});

export const dealerThemeSchema = {
  title: 'Theme',
};
